import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./AlertDetail.scss";
import {
  DetailHomeIcon,
  DetailOptionIcon,
  DetailArrowBackIcon,
  DetailCopyIcon,
  DetailStarLightIcon,
  DetailStarDarkIcon,
} from "../../common/Icon/AIAlerts";
import {
  alertDetailLoad,
  getAlertDetailData,
  getAlertDetailLoading,
  getAlertDetailError,
} from "../../../appRedux/ducks/alertDetail";
import { showSnackbar } from "../../../appRedux/ducks/snackbar";
import BeautifulSkeleton from "../../common/Skeletons/BeautifulSkeleton";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { Tooltip } from "@material-ui/core";
import AITickerDetail from "../AITickerDetail";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { IconButton } from "@material-ui/core";
import { useAuth } from "../../../contexts/Auth";
import { normalizeAndAbbreviate } from "../../utilities";

const AlertDetail = ({
  alertData,
  themeMode,
  contractId: contractIdProp,
  onBack,
}) => {
  const { hasAiOptionsAlertsNeural, loading: authLoading } = useAuth();
  const history = useHistory();
  const dispatch = useDispatch();
  const { contractId: contractIdParam } = useParams();

  const [showTickerDetail, setShowTickerDetail] = useState(false);

  // Get contractId from props, URL params, or alertData
  const contractId =
    contractIdProp ||
    contractIdParam ||
    alertData?.id ||
    alertData?.contractSymbol;

  // Redux selectors
  const alertDetailData = useSelector(getAlertDetailData);
  const loading = useSelector(getAlertDetailLoading);
  const error = useSelector(getAlertDetailError);

  // Fetch alert detail when contractId is available
  useEffect(() => {
    if (contractId) {
      dispatch(alertDetailLoad(contractId));
    }
  }, [dispatch, contractId]);

  // Only use Redux data - no fallback to hardcoded values
  const data = alertDetailData;

  // Check if the data belongs to the current contractId
  const isDataForCurrentContract = data?.contractId === contractId;

  // Don't render if no data available and not loading
  if (!data && !loading && !error) {
    return null;
  }

  // Show loading state with skeletons when loading (even if old data exists)
  if (
    (loading || authLoading) &&
    (!isDataForCurrentContract || !data || !alertData)
  ) {
    return (
      <div className="alert-detail-container">
        {/* Breadcrumb Skeleton */}
        <div className="alert-detail-breadcrumb">
          <div className="breadcrumb-wrapper">
            <div className="breadcrumb-link">
              <div className="breadcrumb-icon">
                <BeautifulSkeleton variant="circular" width={20} height={20} />
              </div>
              <div className="breadcrumb-text">
                <BeautifulSkeleton width={150} height={20} />
              </div>
            </div>
            <div className="breadcrumb-separator">
              <p>/</p>
            </div>
            <div className="breadcrumb-current">
              <div className="breadcrumb-icon">
                <BeautifulSkeleton variant="circular" width={20} height={20} />
              </div>
              <div className="breadcrumb-text">
                <BeautifulSkeleton width={200} height={20} />
              </div>
            </div>
          </div>
        </div>

        {/* Header Section Skeleton */}
        <div className="alert-detail-header-section">
          <div className="alert-header-content">
            <button className="back-button" disabled>
              <div className="back-button-icon">
                <DetailArrowBackIcon />
              </div>
            </button>

            <div className="alert-title-section">
              <div className="alert-title-content">
                {/* Badges Row Skeleton */}
                <div className="alert-badges-row">
                  <BeautifulSkeleton
                    variant="rectangular"
                    width={80}
                    height={28}
                    className="skeleton-rounded-chip"
                  />
                  <BeautifulSkeleton
                    variant="rectangular"
                    width={100}
                    height={28}
                    className="skeleton-rounded-chip"
                  />
                  <BeautifulSkeleton
                    variant="rectangular"
                    width={100}
                    height={28}
                    className="skeleton-rounded-chip"
                  />
                </div>

                {/* Title Row Skeleton */}
                <div className="alert-title-row">
                  <div className="alert-title-text">
                    <BeautifulSkeleton width={300} height={32} />
                    <BeautifulSkeleton
                      width={150}
                      height={20}
                      className="skeleton-spacing-top-medium"
                    />
                  </div>
                  <div className="alert-contract-id">
                    <BeautifulSkeleton width={180} height={24} />
                    <div className="copy-button skeleton-disabled-cursor">
                      <div className="copy-icon">{DetailCopyIcon}</div>
                    </div>
                  </div>
                </div>

                {/* Pricing Section Skeleton */}
                <div className="alert-pricing-section">
                  <div className="alert-pricing-card">
                    <div className="pricing-item">
                      <BeautifulSkeleton width={120} height={16} />
                      <BeautifulSkeleton
                        width={80}
                        height={24}
                        className="skeleton-spacing-top-small"
                      />
                    </div>
                    <div className="pricing-brackets">
                      <div className="bracket-item">
                        <BeautifulSkeleton width={100} height={16} />
                        <BeautifulSkeleton
                          width={60}
                          height={20}
                          className="skeleton-spacing-top-small"
                        />
                      </div>
                      <div className="bracket-item">
                        <BeautifulSkeleton width={100} height={16} />
                        <BeautifulSkeleton
                          width={60}
                          height={20}
                          className="skeleton-spacing-top-small"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timestamp Skeleton */}
        <div className="alert-timestamp-section">
          <BeautifulSkeleton width={250} height={16} />
        </div>

        {/* Options Vitals Section Skeleton */}
        <div className="options-vitals-section">
          <div className="options-vitals-header">
            <BeautifulSkeleton width={150} height={24} />
          </div>

          <div className="options-vitals-card">
            <div className="options-vitals-content">
              {/* Bid-Ask Spread Skeleton */}
              <div className="bid-ask-spread">
                <div className="bid-column">
                  <BeautifulSkeleton width={40} height={16} />
                  <BeautifulSkeleton
                    width={60}
                    height={24}
                    className="skeleton-spacing-top-small"
                  />
                  <BeautifulSkeleton
                    width={40}
                    height={14}
                    className="skeleton-spacing-top-small"
                  />
                </div>

                <div className="spread-line-container">
                  <div className="spread-line"></div>
                </div>

                <div className="mid-column">
                  <BeautifulSkeleton width={40} height={16} />
                  <BeautifulSkeleton
                    width={60}
                    height={24}
                    className="skeleton-spacing-top-small"
                  />
                </div>

                <div className="spread-line-container">
                  <div className="spread-line"></div>
                </div>

                <div className="ask-column">
                  <BeautifulSkeleton width={40} height={16} />
                  <BeautifulSkeleton
                    width={60}
                    height={24}
                    className="skeleton-spacing-top-small"
                  />
                  <BeautifulSkeleton
                    width={40}
                    height={14}
                    className="skeleton-spacing-top-small"
                  />
                </div>
              </div>

              {/* Intrinsic + Extrinsic Skeleton */}
              <div className="value-breakdown">
                <div className="value-item">
                  <BeautifulSkeleton width={80} height={18} />
                  <BeautifulSkeleton
                    width={60}
                    height={24}
                    className="skeleton-spacing-top-small"
                  />
                </div>
                <p className="value-plus">+</p>
                <div className="value-item">
                  <BeautifulSkeleton width={80} height={18} />
                  <BeautifulSkeleton
                    width={60}
                    height={24}
                    className="skeleton-spacing-top-small"
                  />
                </div>
              </div>
            </div>

            {/* Additional Metrics Skeleton */}
            <div className="options-metrics">
              <div className="metrics-row">
                <div className="metrics-group">
                  <div className="metric-item">
                    <BeautifulSkeleton width={70} height={16} />
                    <BeautifulSkeleton
                      width={50}
                      height={20}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                  <div className="metric-item">
                    <BeautifulSkeleton width={70} height={16} />
                    <BeautifulSkeleton
                      width={50}
                      height={20}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                </div>
                <div className="metrics-group">
                  <div className="metric-item">
                    <BeautifulSkeleton width={70} height={16} />
                    <BeautifulSkeleton
                      width={50}
                      height={20}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                  <div className="metric-item">
                    <BeautifulSkeleton width={90} height={16} />
                    <BeautifulSkeleton
                      width={50}
                      height={20}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overview and Unusual Activity Section Skeleton */}
        <div className="overview-activity-section">
          <div className="overview-column">
            <div className="section-header">
              <BeautifulSkeleton width={100} height={24} />
            </div>

            <div className="overview-card">
              <div className="ticker-info">
                <BeautifulSkeleton width={80} height={24} />
                <BeautifulSkeleton
                  width={200}
                  height={18}
                  className="skeleton-spacing-top-small"
                />
              </div>

              <div className="overview-details">
                <div className="overview-item">
                  <div className="overview-item-content">
                    <BeautifulSkeleton width={120} height={16} />
                    <BeautifulSkeleton
                      width={80}
                      height={24}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                </div>
                <div className="overview-item">
                  <div className="overview-item-content">
                    <BeautifulSkeleton width={60} height={16} />
                    <div className="sector-row skeleton-spacing-top-small">
                      <BeautifulSkeleton width={100} height={20} />
                      <BeautifulSkeleton
                        width={60}
                        height={20}
                        className="skeleton-rounded-badge skeleton-spacing-left-small"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Reasoning Card Skeleton */}
              <div className="ai-reasoning-card">
                <div className="ai-reasoning-header">
                  <BeautifulSkeleton width={120} height={24} />
                  <div className="ai-icon">
                    {themeMode === "light" ? (
                      <DetailStarLightIcon />
                    ) : (
                      <DetailStarDarkIcon />
                    )}
                  </div>
                </div>
                <div className="ai-reasoning-content">
                  <ul className="ai-reasoning-list">
                    {[1, 2, 3].map((index) => (
                      <li key={index}>
                        <BeautifulSkeleton
                          width={300}
                          height={18}
                          className="skeleton-full-width skeleton-spacing-bottom-small"
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="unusual-activity-column">
            <div className="section-header">
              <BeautifulSkeleton width={150} height={24} />
              <BeautifulSkeleton
                width={100}
                height={16}
                className="skeleton-spacing-top-small"
              />
            </div>

            <div className="unusual-activity-card">
              <div className="volume-comparison">
                <div className="volume-header">
                  <BeautifulSkeleton width={180} height={20} />
                  <BeautifulSkeleton
                    width={60}
                    height={20}
                    className="skeleton-spacing-left-small"
                  />
                </div>
              </div>

              <div className="volume-details">
                <div className="volume-detail-item">
                  <div className="volume-detail-content">
                    <BeautifulSkeleton width={60} height={16} />
                    <BeautifulSkeleton
                      width={100}
                      height={20}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                </div>
                <div className="volume-detail-item">
                  <div className="volume-detail-content">
                    <BeautifulSkeleton width={80} height={16} />
                    <BeautifulSkeleton
                      width={100}
                      height={20}
                      className="skeleton-spacing-top-small"
                    />
                  </div>
                </div>
              </div>

              <div className="dark-pool-row">
                <BeautifulSkeleton width={140} height={18} />
                <div className="dark-pool-values">
                  <BeautifulSkeleton width={80} height={20} />
                  <BeautifulSkeleton
                    width={60}
                    height={20}
                    className="skeleton-spacing-left-small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {!hasAiOptionsAlertsNeural && (
          <div className="alert-disclaimer-section">
            <p className="disclaimer-text">
              All data is delayed by 15 minutes on the Free tier.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Show error state
  if (error && !data) {
    return (
      <div className="alert-detail-container">
        <div className="alert-detail-error">
          Error loading alert details. Please try again.
        </div>
      </div>
    );
  }

  // If no data after loading completes, don't render
  if (!data) {
    return null;
  }

  // Extract data from Redux - no fallbacks
  const sentiment = data?.sentiment;
  const strategyType = data?.strategyType;
  const alertText = data?.alertText;
  const expiresIn = data?.expiresIn;
  const contractIdDisplay = data?.contractId;
  const symbol = data?.symbol;
  const buyPrice = data?.buyPrice;
  const takeProfit = data?.takeProfit;
  const stopLoss = data?.stopLoss;
  const lastUpdated = data?.lastUpdated;
  const optionsVitals = data?.optionsVitals;
  const overview = data?.overview;
  const companyName = overview?.companyName;
  const underlyingPrice = overview?.underlyingPrice;
  const sector = overview?.sector;
  const sectorChangePercent = overview?.sectorChangePercent;
  const aiReasoning = data?.aiReasoning || "";
  // Split AI reasoning by periods to create bullet points
  const aiReasoningPoints = aiReasoning
    ? aiReasoning
        .split(".")
        .map((point) => point.trim())
        .filter((point) => point.length > 0)
    : [];
  const unusualActivity = data?.unusualActivity;
  const activityDate = unusualActivity?.asOfDate;
  const volumeVsAverage = unusualActivity?.volumeVsAverage || {};
  const volumePeriodObj = volumeVsAverage?.period || {};
  // Format period: if it's an object, show "Daily" or "Weekly Average" based on what's available
  const volumePeriod =
    volumePeriodObj.daily !== undefined
      ? "Daily"
      : volumePeriodObj.weekly_average !== undefined
        ? "Weekly Average"
        : "";
  const volumePercentage = volumeVsAverage?.percentageChange;
  const todayVolume = volumeVsAverage?.todayVolume;
  const averageVolume = volumeVsAverage?.averageVolume;
  const darkPoolActivity = unusualActivity?.darkPoolActivity || {};
  const darkPoolValue = darkPoolActivity?.value
    ? normalizeAndAbbreviate(darkPoolActivity.value, true)
    : null;
  let darkPoolChange = darkPoolActivity?.percentageChange;
  if (typeof darkPoolChange === "string") {
    // Remove all occurrences of '%' character
    darkPoolChange = darkPoolChange.replace(/%/g, "").trim();
  }

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      history.goBack();
    }
  };

  const handleCopyContract = () => {
    navigator.clipboard
      .writeText(contractIdDisplay || "")
      .then(() => {
        dispatch(showSnackbar("Copied to clipboard", { variant: "success" }));
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        dispatch(
          showSnackbar("Failed to copy to clipboard", { variant: "error" }),
        );
      });
  };

  return (
    <>
      {showTickerDetail ? (
        <AITickerDetail
          ticker={symbol}
          alertText={alertText}
          contractId={contractId}
          onBackToAlert={() => setShowTickerDetail(false)}
        />
      ) : (
        <div className="alert-detail-container">
          <div className="alert-detail-breadcrumb">
            <div className="breadcrumb-wrapper">
              <a
                href="/ai-options-alerts/live"
                className="breadcrumb-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (onBack) {
                    onBack();
                  } else {
                    history.push("/ai-options-alerts/live");
                  }
                }}
              >
                <div className="breadcrumb-icon">
                  <DetailHomeIcon />
                </div>
                <div className="breadcrumb-text">
                  <p>AI Options Alerts</p>
                </div>
              </a>
              <div className="breadcrumb-separator">
                <p>/</p>
              </div>
              <div className="breadcrumb-current">
                <div className="breadcrumb-icon">
                  <DetailOptionIcon />
                </div>
                <div className="breadcrumb-text">
                  <p>{alertText || ""}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="alert-detail-header-section">
            <div className="alert-header-content">
              <button className="back-button" onClick={handleBack}>
                <div className="back-button-icon">
                  <DetailArrowBackIcon />
                </div>
              </button>

              <div className="alert-title-section">
                <div className="alert-title-content">
                  <div className="alert-badges-row">
                    {sentiment && (
                      <div
                        className={`alert-chip alert-chip-${sentiment.toLowerCase()}`}
                      >
                        <p>{sentiment}</p>
                      </div>
                    )}
                    {strategyType && (
                      <div className="alert-chip alert-chip-outlined">
                        <p>{strategyType}</p>
                      </div>
                    )}
                    {symbol && (
                      <div className="view-symbol-button-wrapper">
                        <div
                          className="view-symbol-button"
                          onClick={() => setShowTickerDetail(true)}
                        >
                          <div className="view-symbol-button-content">
                            <p>View {symbol}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="alert-title-row">
                    <div className="alert-title-text">
                      {alertText && (
                        <p className="alert-main-title">{alertText}</p>
                      )}
                      {expiresIn && (
                        <p className="alert-expires">Expires in {expiresIn}</p>
                      )}
                    </div>
                    <div className="alert-contract-id">
                      <p>{contractIdDisplay || ""}</p>
                      <IconButton
                        size="small"
                        className="copy-icon-button"
                        onClick={() => handleCopyContract()}
                      >
                        <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </div>
                  </div>

                  <div className="alert-pricing-section">
                    <div className="alert-pricing-card">
                      <div className="pricing-item">
                        {buyPrice && (
                          <>
                            <p className="pricing-label">AI Buy Price</p>
                            <p className="pricing-value">{buyPrice}</p>
                          </>
                        )}
                      </div>
                      <div className="pricing-brackets">
                        <div className="bracket-item">
                          <p className="bracket-label">Take Profit</p>
                          <p
                            className={`bracket-value ${!takeProfit ? "bracket-disabled" : ""}`}
                          >
                            {takeProfit}
                          </p>
                        </div>
                        <div className="bracket-item">
                          <p className="bracket-label">Stop Loss</p>
                          <p
                            className={`bracket-value ${!stopLoss ? "bracket-disabled" : ""}`}
                          >
                            {stopLoss}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="alert-timestamp-section">
            {lastUpdated && <p className="timestamp-text">{lastUpdated}</p>}
          </div>

          <div className="options-vitals-section">
            <div className="options-vitals-header">
              <p className="options-vitals-title">Options Vitals</p>
            </div>

            <div className="options-vitals-card">
              <div className="options-vitals-content">
                {/* Bid-Ask Spread */}
                <div className="bid-ask-spread">
                  {optionsVitals && (
                    <>
                      <div className="bid-column">
                        <p className="spread-label">Bid</p>
                        <p className="spread-value">
                          {optionsVitals.bid || ""}
                        </p>
                        <p className="spread-size">
                          ×{optionsVitals.bidSize || ""}
                        </p>
                      </div>

                      <div className="spread-line-container">
                        <div className="spread-line"></div>
                      </div>

                      <div className="mid-column">
                        <p className="spread-label">Mid</p>
                        <p className="spread-value">
                          {optionsVitals.mid || ""}
                        </p>
                      </div>

                      <div className="spread-line-container">
                        <div className="spread-line"></div>
                      </div>

                      <div className="ask-column">
                        <p className="spread-label text-right">Ask</p>
                        <p className="spread-value">
                          {optionsVitals.ask || ""}
                        </p>
                        <p className="spread-size text-right">
                          ×{optionsVitals.askSize || ""}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Intrinsic + Extrinsic */}
                {optionsVitals && (
                  <div className="value-breakdown">
                    <div className="value-item">
                      <p className="value-label">
                        Intrinsic
                        <Tooltip
                          placement="bottom"
                          title={
                            <div className="custom-AI-tooltip">
                              <div className="custom-AI-tooltip-header">
                                Intrinsic
                              </div>
                              <div className="custom-AI-tooltip-content">
                                Intrinsic value is how much an option is "in the
                                money."
                                <br />
                                <br />
                                For a call option, this is the amount the stock
                                price is above the strike price.For a put
                                option, this is the amount the stock price is
                                below the strike price.
                                <br />
                                <br />
                                If the stock price is not beyond the strike
                                price, the option has no intrinsic value.
                              </div>
                              <div className="custom-AI-tooltip-footer">
                                Source: Adapted from "Characteristics and Risks
                                of Standardized Options" (CRSO) and industry
                                practice.
                              </div>
                            </div>
                          }
                        >
                          <InfoOutlinedIcon className="stat-info-icon" />
                        </Tooltip>
                      </p>
                      <p className="value-amount">
                        {optionsVitals.intrinsic || ""}
                      </p>
                    </div>
                    <p className="value-plus">+</p>
                    <div className="value-item">
                      <p className="value-label">
                        Extrinsic
                        <Tooltip
                          placement="bottom"
                          title={
                            <div className="custom-AI-tooltip">
                              <div className="custom-AI-tooltip-header">
                                Extrinsic
                              </div>
                              <div className="custom-AI-tooltip-content">
                                Extrinsic value (time value) is whatever is left
                                in the option's price after subtracting the
                                intrinsic value.
                                <br />
                                <br />
                                It reflects factors like time until expiration,
                                volatility, and interest rates.
                              </div>
                              <div className="custom-AI-tooltip-footer">
                                Source: Adapted from "Characteristics and Risks
                                of Standardized Options" (CRSO) and industry
                                practice.
                              </div>
                            </div>
                          }
                        >
                          <InfoOutlinedIcon className="stat-info-icon" />
                        </Tooltip>
                      </p>
                      <p className="value-amount">
                        {optionsVitals.extrinsic || ""}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Metrics */}
              {optionsVitals && (
                <div className="options-metrics">
                  <div className="metrics-row">
                    <div className="metrics-group">
                      <div className="metric-item">
                        <p className="metric-label">Bid Size</p>
                        <p className="metric-value">
                          {optionsVitals.bidSize || ""}
                        </p>
                      </div>
                      <div className="metric-item">
                        <p className="metric-label">Ask Size</p>
                        <p className="metric-value">
                          {optionsVitals.askSize || ""}
                        </p>
                      </div>
                    </div>
                    <div className="metrics-group">
                      <div className="metric-item">
                        <p className="metric-label">Volume</p>
                        <p className="metric-value">
                          {optionsVitals.volume || ""}
                        </p>
                      </div>
                      <div className="metric-item">
                        <p className="metric-label">Open Interest</p>
                        <p className="metric-value">
                          {optionsVitals.openInterest || ""}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Overview and Unusual Activity Section */}
          <div className="overview-activity-section">
            <div className="overview-column">
              <div className="section-header">
                <p className="section-title">Overview</p>
              </div>

              <div className="overview-card">
                <div className="ticker-info">
                  {symbol && <p className="ticker-symbol">{symbol}</p>}
                  {companyName && <p className="company-name">{companyName}</p>}
                </div>

                <div className="overview-details">
                  {underlyingPrice && (
                    <div className="overview-item">
                      <div className="overview-item-content">
                        <p className="overview-label">Underlying Price</p>
                        <div className="overview-value-wrapper">
                          <p className="overview-value">{underlyingPrice}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {sector && (
                    <div className="overview-item">
                      <div className="overview-item-content">
                        <p className="overview-label">Sector</p>
                        <div className="sector-row">
                          <p className="sector-name">{sector}</p>
                          {sectorChangePercent && (
                            <div className="sector-chip">
                              <p>{sectorChangePercent}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {aiReasoningPoints && aiReasoningPoints.length > 0 && (
                  <div className="ai-reasoning-card">
                    <div className="ai-reasoning-header">
                      <p className="ai-reasoning-title">AI Reasoning</p>
                      <div className="ai-icon">
                        {themeMode === "light" ? (
                          <DetailStarLightIcon />
                        ) : (
                          <DetailStarDarkIcon />
                        )}
                      </div>
                    </div>
                    <div className="ai-reasoning-content">
                      {hasAiOptionsAlertsNeural ? (
                        <ul className="ai-reasoning-list">
                          {aiReasoningPoints.map((point, index) => (
                            <li key={index}>{point}</li>
                          ))}
                        </ul>
                      ) : (
                        "Unavaliable for free users"
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="unusual-activity-column">
              <div className="section-header">
                <p className="section-title">Unusual Activity</p>
                {activityDate && <p className="section-date">{activityDate}</p>}
              </div>

              {unusualActivity && (
                <div className="unusual-activity-card">
                  {volumePeriod && (
                    <div className="volume-comparison">
                      <div className="volume-header">
                        <p className="volume-label">{volumePeriod}</p>
                        {volumePercentage && (
                          <p className="volume-percentage">
                            {volumePercentage}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {(todayVolume || averageVolume) && (
                    <div className="volume-details">
                      {todayVolume && (
                        <div className="volume-detail-item">
                          <div className="volume-detail-content">
                            <p className="volume-detail-label">Today</p>
                            <p className="volume-detail-value">{todayVolume}</p>
                          </div>
                        </div>
                      )}
                      {averageVolume && (
                        <div className="volume-detail-item">
                          <div className="volume-detail-content">
                            <p className="volume-detail-label">7D Average</p>
                            <p className="volume-detail-value">
                              {averageVolume}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {(darkPoolValue || darkPoolChange) && (
                    <div className="dark-pool-row">
                      <p className="dark-pool-label">Dark Pool Activity</p>
                      <div className="dark-pool-values">
                        {darkPoolValue && (
                          <p className="dark-pool-count">{darkPoolValue}</p>
                        )}
                        {darkPoolChange && (
                          <p
                            className={`dark-pool-change ${
                              Number(darkPoolChange) > 0
                                ? "positive"
                                : Number(darkPoolChange) < 0
                                  ? "negative"
                                  : ""
                            }`}
                          >
                            {darkPoolChange}%
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {!hasAiOptionsAlertsNeural && (
            <div className="alert-disclaimer-section">
              <p className="disclaimer-text">
                All data is delayed by 15 minutes on the Free tier.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AlertDetail;
